import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/kfmApi";
import { createRoomClient } from "@/lib/ablyRoom";
import {
  Users, Copy, Send, Sparkles, BookOpen, NotebookPen, Loader2,
  Power, LogOut, Crown, MessageCircle, Radio,
} from "lucide-react";
import { toast } from "sonner";

export default function StudyRoom() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [room, setRoom] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [members, setMembers] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [notes, setNotes] = useState("");
  const [passageDraft, setPassageDraft] = useState("");
  const [passage, setPassage] = useState("");
  const [section, setSection] = useState("");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiAnswers, setAiAnswers] = useState([]);
  const [askInput, setAskInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [ended, setEnded] = useState(false);
  const [conn, setConn] = useState("connecting");

  const chanRef = useRef(null);
  const clientRef = useRef(null);
  const notesTimer = useRef(null);
  const chatIds = useRef(new Set());
  const chatEndRef = useRef(null);

  const isHost = !!(room && user && room.host_id === user.id);

  const upsertAnswer = useCallback((answer_id, patch) => {
    setAiAnswers((prev) => {
      const i = prev.findIndex((a) => a.answer_id === answer_id);
      if (i === -1) return [...prev.slice(-4), { answer_id, asker: "", prompt: "", text: "", done: false, ...patch }];
      const next = [...prev];
      next[i] = { ...next[i], ...patch, text: patch.appendText != null ? next[i].text + patch.appendText : (patch.text != null ? patch.text : next[i].text) };
      return next;
    });
  }, []);

  // Suppress ONLY the benign Ably "Connection closed" (80017) teardown artifact that can
  // fire when the client is closed mid-connect on unmount — it must not trip the dev overlay.
  useEffect(() => {
    const benign = (m) => typeof m === "string" && (m.includes("Connection closed") || m.includes("80017"));
    const onRej = (e) => { const m = e?.reason?.message || String(e?.reason || ""); if (benign(m)) { e.preventDefault(); e.stopImmediatePropagation?.(); } };
    const onErr = (e) => { const m = e?.error?.message || e?.message || ""; if (benign(m)) { e.preventDefault(); e.stopImmediatePropagation?.(); } };
    window.addEventListener("unhandledrejection", onRej, true);
    window.addEventListener("error", onErr, true);
    return () => {
      window.removeEventListener("unhandledrejection", onRej, true);
      window.removeEventListener("error", onErr, true);
    };
  }, []);

  // Load authoritative room state, then connect to Ably for live events.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${code}`);
        if (!active) return;
        setRoom(data);
        setNotes(data.notes || "");
        setPassage(data.passage_ref || "");
        setPassageDraft(data.passage_ref || "");
        setSection(data.section || "");
        setAiEnabled(!!data.ai_enabled);
        const seed = data.chat || [];
        seed.forEach((m) => chatIds.current.add(m.id));
        setChat(seed);
      } catch (e) {
        if (active) setLoadErr(formatApiError(e.response?.data?.detail));
        return;
      }

      const { client, channel } = createRoomClient(code);
      clientRef.current = client;
      chanRef.current = channel;
      client.connection.on((s) => setConn(s.current));
      // Swallow benign connection errors (e.g. closing mid-connect on unmount).
      try { client.connection.on("failed", () => {}); } catch (e) {}

      try {
        if (!active) return;
        await channel.subscribe((msg) => {
          const d = msg.data || {};
          switch (msg.name) {
            case "chat.message":
              if (!chatIds.current.has(d.id)) {
                chatIds.current.add(d.id);
                setChat((c) => [...c, d]);
              }
              break;
            case "passage.changed":
              setPassage(d.passage_ref || "");
              setSection(d.section || "");
              setPassageDraft(d.passage_ref || "");
              break;
            case "ai.toggled":
              setAiEnabled(!!d.enabled);
              break;
            case "ai.started":
              upsertAnswer(d.answer_id, { asker: d.asker, prompt: d.prompt, text: "", done: false });
              break;
            case "ai.chunk":
              upsertAnswer(d.answer_id, { appendText: d.text || "" });
              break;
            case "ai.completed":
              upsertAnswer(d.answer_id, { done: true, ...(d.text ? { text: d.text } : {}) });
              break;
            case "notes.updated":
              if (d.by !== user?.id) setNotes(d.notes || "");
              break;
            case "room.ended":
              setEnded(true);
              break;
            default:
              break;
          }
        });

        const refreshMembers = async () => {
          try {
            const list = await channel.presence.get();
            const seen = new Map();
            (list || []).forEach((m) => seen.set(m.clientId, m));
            setMembers([...seen.values()]);
          } catch (e) { /* ignore */ }
        };
        await channel.presence.subscribe(refreshMembers);
        if (!active) return;
        await channel.presence.enter({ name: user?.name || "Friend" });
        await refreshMembers();
      } catch (e) {
        // Connection was torn down (e.g. user left before it finished connecting) — safe to ignore.
      }
    })();

    return () => {
      active = false;
      const ch = chanRef.current;
      const cl = clientRef.current;
      chanRef.current = null;
      clientRef.current = null;
      try { ch?.unsubscribe?.(); } catch (e) {}
      try { ch?.presence?.unsubscribe?.(); } catch (e) {}
      try {
        const p = ch?.presence?.leave?.();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (e) {}
      try { cl?.close?.(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput("");
    try { await api.post(`/rooms/${code}/chat`, { text }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNotes = (val) => {
    setNotes(val);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => {
      api.post(`/rooms/${code}/notes`, { notes: val }).catch(() => {});
    }, 700);
  };

  const setPassageHost = async () => {
    try { await api.post(`/rooms/${code}/passage`, { passage_ref: passageDraft.trim(), section: section.trim() }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleAi = async () => {
    try { await api.post(`/rooms/${code}/ai-toggle`, { enabled: !aiEnabled }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ask = async () => {
    const prompt = askInput.trim();
    if (!prompt) return;
    setAskInput("");
    setAsking(true);
    try { await api.post(`/rooms/${code}/ask`, { prompt }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAsking(false); }
  };

  const endRoom = async () => {
    try { await api.post(`/rooms/${code}/end`); nav("/rooms"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyInvite = async () => {
    const link = `${window.location.origin}/rooms/${code}`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch (e) {
      toast.message("Share this room link", { description: link });
    }
  };

  if (loadErr) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="room-error">
        <p className="text-slate-400">{loadErr}</p>
        <button onClick={() => nav("/rooms")} className="mt-4 text-sm font-semibold text-[#00E5FF]">Back to rooms</button>
      </div>
    );
  }
  if (!room) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4" data-testid="study-room">
      {ended && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm px-4 py-3" data-testid="room-ended-banner">
          The host has ended this study room. You can still read the notes below.
        </div>
      )}

      {/* Header */}
      <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-4 sm:p-5 flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl text-white truncate">{room.title || "Study room"}</h1>
            {isHost && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" /> Host</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><Radio className={`w-3 h-3 ${conn === "connected" ? "text-emerald-500" : "text-slate-500"}`} /> {conn}</span>
            <button data-testid="room-copy-invite" onClick={copyInvite} className="inline-flex items-center gap-1 font-semibold text-[#00E5FF] hover:underline">
              <Copy className="w-3.5 h-3.5" /> Code {code}
            </button>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-slate-400" data-testid="room-presence">
            <Users className="w-4 h-4" />
            <span className="font-semibold text-slate-300">{members.length}</span>
            <span className="hidden sm:inline text-slate-500 truncate max-w-[220px]">
              {members.map((m) => m.data?.name || "Friend").join(", ")}
            </span>
          </div>
          {isHost ? (
            <button data-testid="room-end-btn" onClick={endRoom} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-400 border border-red-500/30 rounded-full px-3.5 py-1.5 hover:bg-red-500/10">
              <Power className="w-4 h-4" /> End
            </button>
          ) : (
            <button data-testid="room-leave-btn" onClick={() => nav("/rooms")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-400 border border-[#29344D] rounded-full px-3.5 py-1.5 hover:bg-[#0B0F16]">
              <LogOut className="w-4 h-4" /> Leave
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LEFT: Passage + AI */}
        <div className="space-y-4">
          {/* Passage */}
          <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-5">
            <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Passage</div>
            {isHost ? (
              <div className="flex gap-2">
                <input data-testid="room-passage-field" value={passageDraft} onChange={(e) => setPassageDraft(e.target.value)}
                  placeholder="e.g. Psalm 23:1-6" className="flex-1 text-sm border border-[#29344D] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30" />
                <button data-testid="room-passage-set" onClick={setPassageHost} className="rounded-full bg-[#00E5FF] text-[#07090E] text-sm font-semibold px-4 hover:bg-[#00B8CC]">Set for all</button>
              </div>
            ) : (
              <div data-testid="room-passage-view" className="text-lg text-slate-200">{passage || "Waiting for the host to open a passage…"}</div>
            )}
            {section && <div className="text-xs text-slate-500 mt-1">Section: {section}</div>}
          </div>

          {/* AI companion */}
          <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Virtual study companion</div>
              {isHost ? (
                <button data-testid="room-ai-toggle" onClick={toggleAi}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${aiEnabled ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "text-slate-400 border-[#29344D] hover:bg-[#0B0F16]"}`}>
                  <Power className="w-3.5 h-3.5" /> {aiEnabled ? "On for the group" : "Off"}
                </button>
              ) : (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${aiEnabled ? "bg-emerald-500/10 text-emerald-400" : "bg-[#141B29] text-slate-400"}`}>
                  {aiEnabled ? "On" : "Off"}
                </span>
              )}
            </div>

            <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="room-ai-answers">
              {aiAnswers.length === 0 && (
                <p className="text-sm text-slate-500">
                  {aiEnabled ? "Ask a question and the whole group will see the answer." : (isHost ? "Turn the companion on to let the group ask questions together." : "The host hasn't turned the study companion on yet.")}
                </p>
              )}
              {aiAnswers.map((a) => (
                <div key={a.answer_id} className="rounded-xl bg-[#0E131F] p-3">
                  {a.prompt && <div className="text-xs text-slate-400 mb-1"><span className="font-semibold text-slate-400">{a.asker || "Someone"} asked:</span> {a.prompt}</div>}
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{a.text}{!a.done && <span className="inline-block w-1.5 h-4 bg-[#00E5FF]/60 ml-0.5 animate-pulse align-middle" />}</div>
                </div>
              ))}
            </div>

            {aiEnabled && (
              <div className="flex gap-2 mt-3">
                <input data-testid="room-ask-input" value={askInput} onChange={(e) => setAskInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && ask()}
                  placeholder="Ask the study companion for the group…"
                  className="flex-1 text-sm border border-[#29344D] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30" />
                <button data-testid="room-ask-btn" onClick={ask} disabled={asking}
                  className="rounded-full bg-[#00E5FF] text-[#07090E] text-sm font-semibold px-4 hover:bg-[#00B8CC] disabled:opacity-60 inline-flex items-center gap-1.5">
                  {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Ask
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Notes + Chat */}
        <div className="space-y-4">
          {/* Shared notes */}
          <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-5">
            <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2 flex items-center gap-1.5"><NotebookPen className="w-3.5 h-3.5" /> Shared notes</div>
            <textarea data-testid="room-notes" value={notes} onChange={(e) => saveNotes(e.target.value)} rows={7}
              placeholder="Everyone can write here — it syncs to the whole group."
              className="w-full text-sm text-slate-200 border border-[#29344D] rounded-xl px-3 py-2.5 resize-y focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30" />
          </div>

          {/* Chat */}
          <div className="bg-[#0E131F] border border-[#1E273D] rounded-2xl p-5 flex flex-col">
            <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Chat</div>
            <div className="space-y-2 h-56 overflow-y-auto mb-3 pr-1" data-testid="room-chat">
              {chat.length === 0 && <p className="text-sm text-slate-500">Say hello to the group.</p>}
              {chat.map((m) => (
                <div key={m.id} className="text-sm">
                  <span className={`font-semibold ${m.sender_id === user?.id ? "text-[#00E5FF]" : "text-slate-300"}`}>{m.sender_name}: </span>
                  <span className="text-slate-300">{m.text}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2">
              <input data-testid="room-chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Message the group…"
                className="flex-1 text-sm border border-[#29344D] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/30" />
              <button data-testid="room-chat-send" onClick={sendChat} className="rounded-full bg-[#00E5FF] text-[#07090E] px-4 hover:bg-[#00B8CC]"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
